export type ReachabilityStatus = "reachable" | "degraded" | "blocked";

export type ReachabilityErrorCode =
  | "timeout"
  | "dns_error"
  | "tls_error"
  | "network_error"
  | "unknown_error"
  | `http_${number}`;

export type ReachabilityProbeEndpoint = {
  id: string;
  url: string;
  method?: "GET" | "POST" | "HEAD" | "OPTIONS";
  headers?: Record<string, string>;
  body?: unknown;
  timeoutMs?: number;
};

export type ReachabilityProbeResult = {
  id: string;
  url: string;
  status: ReachabilityStatus;
  errorCode: ReachabilityErrorCode | null;
  httpStatus: number | null;
  latencyMs: number;
  checkedAt: string;
};

const DEFAULT_TIMEOUT_MS = 5000;

function classifyNetworkError(error: unknown): ReachabilityErrorCode {
  if (error instanceof DOMException && error.name === "AbortError") {
    return "timeout";
  }

  const message =
    typeof error === "object" && error && "message" in error
      ? String((error as { message?: unknown }).message || "")
      : String(error || "");

  const lower = message.toLowerCase();
  if (
    lower.includes("name_not_resolved") ||
    lower.includes("dns") ||
    lower.includes("could not resolve")
  ) {
    return "dns_error";
  }
  if (
    lower.includes("ssl") ||
    lower.includes("tls") ||
    lower.includes("certificate") ||
    lower.includes("cert_")
  ) {
    return "tls_error";
  }
  if (lower.includes("timeout") || lower.includes("timed out")) {
    return "timeout";
  }
  if (lower.includes("network") || lower.includes("failed to fetch")) {
    return "network_error";
  }
  return "unknown_error";
}

export async function probeEndpoint(
  endpoint: ReachabilityProbeEndpoint,
): Promise<ReachabilityProbeResult> {
  const startedAt = performance.now();
  const timeoutMs = endpoint.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(endpoint.url, {
      method: endpoint.method || "GET",
      headers: endpoint.headers,
      body:
        endpoint.body == null
          ? undefined
          : typeof endpoint.body === "string"
            ? endpoint.body
            : JSON.stringify(endpoint.body),
      signal: controller.signal,
    });

    const latencyMs = Math.round(performance.now() - startedAt);
    const isServerFailure = response.status >= 500;

    return {
      id: endpoint.id,
      url: endpoint.url,
      status: isServerFailure ? "degraded" : "reachable",
      errorCode: isServerFailure
        ? (`http_${response.status}` as ReachabilityErrorCode)
        : null,
      httpStatus: response.status,
      latencyMs,
      checkedAt: new Date().toISOString(),
    };
  } catch (error) {
    const latencyMs = Math.round(performance.now() - startedAt);
    return {
      id: endpoint.id,
      url: endpoint.url,
      status: "blocked",
      errorCode: classifyNetworkError(error),
      httpStatus: null,
      latencyMs,
      checkedAt: new Date().toISOString(),
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function runReachabilityProbe(
  endpoints: ReachabilityProbeEndpoint[],
): Promise<ReachabilityProbeResult[]> {
  return Promise.all(endpoints.map((endpoint) => probeEndpoint(endpoint)));
}
